/**
 * API центру сповіщень (notifications.md, розд. 5): `/api/v1/notifications`. Лише свої пункти —
 * умову адресата ставить сервер. Ключі кешу — під `['p', …]`: сигнал `reset` потоку подій і
 * повторний вхід скидають їх разом з рештою даних платформи (`platform/events.ts`).
 */
import type {
  NotificationsPage,
  NotificationsQuietSummary,
  NotificationsReadBody,
  NotificationsReadResult,
  NotificationsUnreadBody,
  NotificationsUnreadResult,
  NotificationView,
} from '@clientline/contracts';
import { api } from '../../platform/api.ts';

export type NotificationState = 'unread' | 'all';

export const notificationKeys = {
  all: ['p', 'ntf'] as const,
  count: ['p', 'ntf', 'count'] as const,
  lists: ['p', 'ntf', 'list'] as const,
  list: (state: NotificationState) => ['p', 'ntf', 'list', state] as const,
};

const BASE = '/api/v1/notifications';
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export const fetchUnreadCount = (signal?: AbortSignal) =>
  api<{ unread: number }>('GET', `${BASE}/count`, withSignal(signal)).then((r) => r.unread);

/** Зведення за час тиші (NTF-27). */
export const fetchQuietSummary = () =>
  api<NotificationsQuietSummary>('GET', `${BASE}/quiet-summary`);

/** Сторінка за курсором; розмір — типовий сервера (30, NTF-13). */
export function fetchNotifications(
  state: NotificationState,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<NotificationsPage> {
  const params = new URLSearchParams({ state });
  if (cursor) params.set('cursor', cursor);
  return api<NotificationsPage>('GET', `${BASE}?${params}`, withSignal(signal));
}

/** Один пункт — для тосту (`404` — чужий чи вже очищений). */
export const fetchNotification = (id: string, signal?: AbortSignal) =>
  api<NotificationView>('GET', `${BASE}/${encodeURIComponent(id)}`, withSignal(signal));

export const markRead = (body: NotificationsReadBody) =>
  api<NotificationsReadResult>('POST', `${BASE}/read`, { body });

export const markUnread = (body: NotificationsUnreadBody) =>
  api<NotificationsUnreadResult>('POST', `${BASE}/unread`, { body });
